import React, { useRef, useEffect } from "react";
import Chart from "chart.js/auto";

/**
 * StackedBarChart Component
 * @param {Object} props
 * @param {Array} props.datasets - Chart datasets
 * @param {Array} props.labels - X-axis labels for days of the week
 * @param {number} props.maxValue - Maximum value for Y-axis
 * @param {string} props.title - Chart title
 * @param {Object} props.containerStyle - Style for the container div
 */
const BarChart = ({
  datasets = [],
  labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  maxValue = 15,
  title = "Average Score by Weekday",
  containerStyle = { height: "400px", width: "100%" },
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const containerRef = useRef(null);

  const findMaxValueIndices = (datasets) => {
    const maxIndices = [];

    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      let maxValue = 0;
      let maxDatasetIndex = -1;
      let currentTotal = 0;

      for (
        let datasetIndex = 0;
        datasetIndex < datasets.length;
        datasetIndex++
      ) {
        const value = datasets[datasetIndex].data[dayIndex];
        if (value > 0) {
          currentTotal += value;
          if (currentTotal > maxValue) {
            maxValue = currentTotal;
            maxDatasetIndex = datasetIndex;
          }
        }
      }

      maxIndices.push(maxDatasetIndex);
    }

    return maxIndices;
  };

  const addCSSVerticalSeparators = () => {
    if (!chartInstance.current || !containerRef.current) return;

    // Remove existing separators
    containerRef.current
      .querySelectorAll(".chart-tick-separator")
      .forEach((el) => el.remove());

    const xAxis = chartInstance.current.scales.x;
    if (!xAxis) return;

    for (let i = 0; i < xAxis.ticks.length; i++) {
      const getDynamicOffset = () => {
        return window.innerWidth < 768 ? 8 : 8; // Smaller offset for small screens
      };

      const x =
        xAxis.getPixelForTick(i) -
        xAxis.getPixelForTick(0) +
        getDynamicOffset();

      const bottomPosition = xAxis.height - 15; // Add a small offset for alignment

      const separator = document.createElement("div");
      separator.className = "chart-tick-separator";
      separator.style.cssText = ` 
        position: absolute;
        left: ${x}px;
        bottom: ${bottomPosition}px;
        width: 1px;
        height: 9px;
        background-color: #B5BBC0;
        pointer-events: none;
        
      `;

      containerRef.current.appendChild(separator);
    }

    // Add extra separator at the end
    const lastTickPosition = xAxis.getPixelForTick(xAxis.ticks.length - 1);
    const extraSeparatorX =
      lastTickPosition + (xAxis.getPixelForTick(1) - xAxis.getPixelForTick(0)); // Adding width of one tick
    const extraBottomPosition = xAxis.height - 15;

    const extraSeparator = document.createElement("div");
    extraSeparator.className = "chart-tick-separator";
    extraSeparator.style.cssText = ` 
      position: absolute;
      left: ${extraSeparatorX - xAxis.getPixelForTick(0) + 8}px;
      bottom: ${extraBottomPosition}px;
      width: 1px;
      height: 9px;
      background-color: #B5BBC0;
      pointer-events: none;
      
    `;

    containerRef.current.appendChild(extraSeparator);
  };

  const debounce = (func, wait) => {
    let timeout;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };

  useEffect(() => {
    // Add resize listener
    const handleResize = debounce(() => {
      if (chartInstance.current) {
        chartInstance.current.update();
        setTimeout(addCSSVerticalSeparators, 200);
      }
    }, 100);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (chartRef.current) {
      // Clean up previous chart instance
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }

      // Process and prepare datasets
      const processedDatasets = [...datasets];

      // Sort datasets by maximum value (ascending)
      processedDatasets.sort((a, b) => {
        const maxA = Math.max(...a.data);
        const maxB = Math.max(...b.data);
        return maxA - maxB;
      });

      // Find top indices for rounded corners
      const topIndices = findMaxValueIndices(processedDatasets);

      // Set border properties for all datasets
      processedDatasets.forEach((dataset, datasetIndex) => {
        // Add border properties
        dataset.borderWidth = dataset.borderWidth || 1;
        dataset.borderColor = dataset.borderColor || "#121E29";

        // Add rounded corners for top segments
        dataset.borderRadius = dataset.data.map((value, dayIndex) => {
          if (datasetIndex === topIndices[dayIndex]) {
            return {
              topLeft: 4,
              topRight: 4,
              bottomLeft: 0,
              bottomRight: 0,
            };
          } else {
            return 0;
          }
        });
      });

      // Create new Chart instance
      const ctx = chartRef.current.getContext("2d");
      chartInstance.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: labels,
          datasets: processedDatasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          elements: {
            bar: {
              borderSkipped: false, // Ensures borders are drawn on all sides
            },
          },
          animation: {
            onProgress: function (animation) {
              const ctx = this.ctx;
              const chart = this;

              // Calculate cumulative values for proper positioning
              const cumulativeValues = {};

              // Initialize cumulative values for each label
              labels.forEach((_, labelIndex) => {
                cumulativeValues[labelIndex] = 0;
              });

              this.data.datasets.forEach((dataset, datasetIndex) => {
                const meta = this.getDatasetMeta(datasetIndex);
                if (!meta.hidden) {
                  meta.data.forEach((element, index) => {
                    const dataValue = dataset.data[index];

                    // Only show text for values greater than 0.05
                    if (dataValue > 0.05) {
                      // Calculate position using scales for more accurate positioning
                      const xScale = chart.scales.x;
                      const yScale = chart.scales.y;

                      const centerX = xScale.getPixelForTick(index);

                      // Calculate centerY based on cumulative values
                      const segmentStart = cumulativeValues[index];
                      const segmentEnd = segmentStart + dataValue;
                      const centerY = yScale.getPixelForValue(
                        (segmentStart + segmentEnd) / 2,
                      );

                      const segmentHeight = Math.abs(
                        yScale.getPixelForValue(segmentStart) -
                          yScale.getPixelForValue(segmentEnd),
                      );

                      // Set text color based on dataset
                      if (dataset.label === "Goals") {
                        ctx.fillStyle = "#E6ECF1";
                      } else {
                        ctx.fillStyle = "#121E29";
                      }

                      // Adjust font size based on segment height
                      let fontSize = 12;
                      if (segmentHeight < 25) {
                        fontSize = 10;
                      } else if (segmentHeight < 35) {
                        fontSize = 11;
                      }

                      ctx.font = `400 ${fontSize}px Poppins`;
                      ctx.textAlign = "center";
                      ctx.textBaseline = "middle";

                      const formattedValue = dataValue.toFixed(1);

                      // Check if text will fit in the segment
                      const textMetrics = ctx.measureText(formattedValue);
                      const textHeight = fontSize * 1.2;

                      // Only render text if segment is tall enough (minimum 18px)
                      if (segmentHeight >= 10) {
                        ctx.fillText(formattedValue, centerX, centerY);
                      } else if (segmentHeight > 0) {
                        ctx.fillText(
                          formattedValue,
                          centerX,
                          centerY - segmentHeight - 6,
                        );
                      }
                    }

                    // Update cumulative value for this label
                    cumulativeValues[index] += dataValue;
                  });
                }
              });
            },
            onComplete: function () {
              addCSSVerticalSeparators();
            },
          },
          plugins: {
            title: {
              display: true,
              text: title,
              padding: {
                bottom: 16,
              },
              font: {
                family: "Poppins",
                size: 20,
                weight: "600",
                lineHeight: 1.5,
              },
              align: "start",
              color: "#FFFFFF",
            },
            legend: {
              display: true,
              labels: {
                generateLabels: (chart) => {
                  const labelOrder = ["Journal", "Habit", "Task", "Goals"];
                  return labelOrder
                    .map((label) => {
                      const dataset = chart.data.datasets.find(
                        (dataset) => dataset.label === label,
                      );

                      if (dataset) {
                        return {
                          text:
                            "  " +
                            (dataset.label === "Journal"
                              ? "Journal(AM/PM)"
                              : dataset.label) +
                            "     ",
                          font: {
                            family: "Poppins",
                            size: 14,
                            weight: "400",
                            lineHeight: 1.71,
                          },
                          fillStyle: dataset.backgroundColor,
                          pointStyle: "circle",
                          usePointStyle: true,
                          fontColor: "#ffffff",
                          borderWidth: 0,
                          strokeStyle: "transparent",
                          lineWidth: 0,
                          boxWidth: 12,
                          boxHeight: 12,
                          hidden: !chart.isDatasetVisible(
                            chart.data.datasets.indexOf(dataset),
                          ),
                        };
                      }
                      return null;
                    })
                    .filter(Boolean);
                },
                usePointStyle: true,
                borderWidth: 0,
              },
              position: "top",
              align: "start",
              itemMargin: {
                horizontal: 20,
                vertical: 0,
              },
              onClick: function (event, legendItem, legend) {
                const chart = legend.chart;
                let label = legendItem.text.trim();
                if (label === "Journal(AM/PM)") {
                  label = "Journal";
                }
                const datasetIndex = chart.data.datasets.findIndex(
                  (dataset) => dataset.label === label,
                );

                if (datasetIndex !== undefined) {
                  const meta = chart.getDatasetMeta(datasetIndex);
                  meta.hidden = !meta.hidden;
                  chart.update();
                }
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              ticks: {
                color: "#B5BBC0",
                font: {
                  family: "Poppins",
                  weight: 600,
                  size: 16,
                  lineHeight: 1.5,
                },
              },
              grid: {
                display: false,
              },
              border: {
                color: "#2B3C4A",
                width: 1,
              },
            },
            y: {
              stacked: true,
              min: 0,
              max: maxValue,
              ticks: {
                display: false,
                stepSize: 0.5,
              },
              grid: {
                display: false,
              },
              border: {
                display: false,
              },
            },
          },
        },
      });
    }
  }, [datasets, labels, maxValue, title]);

  return (
    <div ref={containerRef} style={{ position: "relative", ...containerStyle }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

export default BarChart;
